import type { ContentHierarchyMode, ContentListQuery, ContentType, Filter, FilterDefinition } from "@vivport/backend/types";

export interface Filters {
  tab?: string;
  include?: string;
  exclude?: string;
  after?: string;
  before?: string;
  createdBy?: string[];
  mailbox?: boolean;
  drives?: boolean;
}

export const MAILBOX_QUERY_TOKEN = 'in:mailbox';
export const DRIVES_QUERY_TOKEN = 'in:drives';

const TAB_PREFIX = 'tab:';
const AFTER_PREFIX = 'after:';
const BEFORE_PREFIX = 'before:';
const CREATED_BY_PREFIX = 'createdBy:';
const CALENDAR_DATE_TOKEN = /^\d{4}-\d{2}-\d{2}$/;

export interface ExploreQueryTokens {
  tabs: string[];
  mailbox: boolean;
  drives: boolean;
  after?: string;
  before?: string;
  exclude: string[];
  createdBy: string[];
  searchText: string;
}

/**
 * Build a search query string from filters object
 */
export function buildQueryString(filters: Filters): string {
  const queryParts: string[] = [];

  if (filters.tab) queryParts.push(`${TAB_PREFIX}${filters.tab}`);
  if (filters.include) queryParts.push(filters.include);
  if (filters.exclude) {
    for (const word of filters.exclude.split(/\s+/).filter(Boolean)) {
      queryParts.push(`-${word}`);
    }
  }
  if (filters.after) queryParts.push(`${AFTER_PREFIX}${filters.after}`);
  if (filters.before) queryParts.push(`${BEFORE_PREFIX}${filters.before}`);

  if (filters.createdBy && filters.createdBy.length > 0) {
    const createdByParts = filters.createdBy.map(searchKey => `${CREATED_BY_PREFIX}${searchKey}`);
    if (createdByParts.length === 1) {
      queryParts.push(createdByParts[0]);
    } else {
      queryParts.push(`(${createdByParts.join(" OR ")})`);
    }
  }

  if (filters.mailbox) queryParts.push(MAILBOX_QUERY_TOKEN);
  if (filters.drives) queryParts.push(DRIVES_QUERY_TOKEN);

  return queryParts.join(" ").trim();
}

/**
 * Classify every whitespace-separated token of an Explore query.
 */
export function tokenizeExploreQuery(query: string): ExploreQueryTokens {
  const tokens: ExploreQueryTokens = { tabs: [], mailbox: false, drives: false, exclude: [], createdBy: [], searchText: '' };
  const words: string[] = [];
  let inGroup = false;

  for (const rawToken of query.trim().split(/\s+/).filter(Boolean)) {
    let token = rawToken;
    let closesGroup = false;
    if (token.startsWith('(')) {
      token = token.slice(1);
      inGroup = true;
    }
    if (token.endsWith(')')) {
      token = token.slice(0, -1);
      closesGroup = true;
    }

    if (token.length === 0 || (inGroup && token === 'OR')) {
      if (closesGroup) inGroup = false;
      continue;
    }

    if (token.startsWith(TAB_PREFIX)) {
      const tab = token.slice(TAB_PREFIX.length);
      if (!tokens.tabs.includes(tab)) tokens.tabs.push(tab);
    } else if (token === MAILBOX_QUERY_TOKEN) {
      tokens.mailbox = true;
    } else if (token === DRIVES_QUERY_TOKEN) {
      tokens.drives = true;
    } else if (token.startsWith(AFTER_PREFIX) && CALENDAR_DATE_TOKEN.test(token.slice(AFTER_PREFIX.length))) {
      tokens.after ??= token.slice(AFTER_PREFIX.length);
    } else if (token.startsWith(BEFORE_PREFIX) && CALENDAR_DATE_TOKEN.test(token.slice(BEFORE_PREFIX.length))) {
      tokens.before ??= token.slice(BEFORE_PREFIX.length);
    } else if (token.startsWith('-') && token.length > 1) {
      tokens.exclude.push(token.slice(1));
    } else if (token.startsWith(CREATED_BY_PREFIX) && token.length > CREATED_BY_PREFIX.length) {
      const key = token.slice(CREATED_BY_PREFIX.length);
      if (!tokens.createdBy.includes(key)) tokens.createdBy.push(key);
    } else {
      words.push(token);
    }

    if (closesGroup) inGroup = false;
  }

  tokens.searchText = words.join(' ');
  return tokens;
}

/**
 * Resolve tab: tokens to the union of their content types using fetched tab-filters.
 * Fail-fast: unknown tokens or missing tabFilters contribute no contentTypes.
 */
export function resolveTabFilterToContentTypes(
  tabs: string[],
  tabFilters: Filter[] | undefined
): {
  contentTypes: ContentType[];
  sections?: FilterDefinition['sections'];
  projection?: FilterDefinition['projection'];
} {
  const contentTypes: ContentType[] = [];
  const seen = new Set<ContentType>();
  const projections = new Set<FilterDefinition['projection']>();
  const sectionHierarchyModes: Record<'document' | 'message', Set<ContentHierarchyMode>> = {
    document: new Set(),
    message: new Set(),
  };

  for (const token of tabs) {
    if (!tabFilters || tabFilters.length === 0) continue;
    const tab = tabFilters.find((f) => f.shortName === token);
    if (!tab?.definition?.contentTypes || !Array.isArray(tab.definition.contentTypes)) continue;

    for (const t of tab.definition.contentTypes as ContentType[]) {
      if (!seen.has(t)) {
        seen.add(t);
        contentTypes.push(t);
      }
    }
    for (const sectionKey of ['document', 'message'] as const) {
      const hierarchyMode = tab.definition.sections?.[sectionKey]?.hierarchyMode;
      if (hierarchyMode) {
        sectionHierarchyModes[sectionKey].add(hierarchyMode);
      }
    }
    projections.add(tab.definition.projection ?? 'CONTENT');
  }

  const sections: NonNullable<FilterDefinition['sections']> = {};
  for (const sectionKey of ['document', 'message'] as const) {
    const hierarchyModes = sectionHierarchyModes[sectionKey];
    if (hierarchyModes.size === 1) {
      sections[sectionKey] = { hierarchyMode: [...hierarchyModes][0] };
      continue;
    }
    if (hierarchyModes.size > 1) {
      sections[sectionKey] = { hierarchyMode: 'ALL' };
    }
  }

  const projection =
    projections.size === 1 ? [...projections][0] : projections.has('CONVERSATION') ? 'CONVERSATION' : undefined;

  return {
    contentTypes,
    sections: Object.keys(sections).length > 0 ? sections : undefined,
    projection,
  };
}

interface NormalizeProjectionContentTypesInput {
  projection?: FilterDefinition['projection'];
  contentTypes?: ContentType[];
  contentTypesExplicitEmpty?: boolean;
}

interface NormalizeProjectionContentTypesResult {
  contentTypes?: ContentType[];
  contentTypesExplicitEmpty?: boolean;
}

/**
 * Enforce projection-specific content type constraints before list queries are sent.
 * CONVERSATION projection only supports MESSAGE content.
 */
export function normalizeProjectionContentTypes(
  input: NormalizeProjectionContentTypesInput
): NormalizeProjectionContentTypesResult {
  if (input.projection !== 'CONVERSATION') {
    return {
      contentTypes: input.contentTypes,
      contentTypesExplicitEmpty: input.contentTypesExplicitEmpty,
    };
  }

  if (input.contentTypesExplicitEmpty) {
    return {
      contentTypes: [],
      contentTypesExplicitEmpty: true,
    };
  }

  return {
    contentTypes: ['MESSAGE'],
    contentTypesExplicitEmpty: undefined,
  };
}

export interface ExploreQueryFilterParseResult {
  contentTypes?: ContentType[];
  sections?: FilterDefinition['sections'];
  projection?: FilterDefinition['projection'];
  contentTypesExplicitEmpty?: boolean;
  search?: string;
  mailbox: boolean;
  drives: boolean;
  after?: string;
  before?: string;
}

/**
 * Parse Explore query text into filter params, including strict tab: token handling.
 */
export function parseExploreQueryFilters(input: {
  query: string;
  tabFilters?: Filter[];
}): ExploreQueryFilterParseResult {
  const tokens = tokenizeExploreQuery(input.query);
  const tabFilterResolution = resolveTabFilterToContentTypes(tokens.tabs, input.tabFilters);
  const contentTypes = tabFilterResolution.contentTypes;

  // Strict fail-fast: explicit invalid/unknown tab token yields empty results (not all content)
  const hasTabToken = tokens.tabs.length > 0;
  const resolvedContentTypes = contentTypes.length > 0 ? contentTypes : hasTabToken ? [] : undefined;
  const contentTypesExplicitEmpty = hasTabToken && contentTypes.length === 0;

  return {
    contentTypes: resolvedContentTypes,
    sections: tabFilterResolution.sections,
    projection: tabFilterResolution.projection,
    contentTypesExplicitEmpty: contentTypesExplicitEmpty || undefined,
    search: tokens.searchText || undefined,
    mailbox: tokens.mailbox,
    drives: tokens.drives,
    after: tokens.after,
    before: tokens.before,
  };
}

function localMidnightIso(calendarDate: string): string {
  const [year, month, day] = calendarDate.split('-').map(Number);
  return new Date(year, month - 1, day).toISOString();
}

export interface ExploreContentListQueryAssemblyInput {
  query: string;
  tabFilters?: Filter[];
  savedDefinition?: FilterDefinition | null;
  orderBy: NonNullable<ContentListQuery['orderBy']>;
  orderDir: NonNullable<ContentListQuery['orderDir']>;
  sortExplicit: boolean;
  cursor: number;
  limit: number;
}

export interface ExploreContentListQueryAssemblyResult {
  request: ContentListQuery;
  state: {
    projection?: FilterDefinition['projection'];
    contentTypes?: ContentType[];
    sections?: FilterDefinition['sections'];
    search?: string;
    mailbox: boolean;
    drives: boolean;
    after?: string;
    before?: string;
  };
}

/**
 * Build normalized Explore content list query params from text query + saved filter.
 * Keeps projection orthogonal (top-level) and sections scoped to filter sections.
 */
export function buildExploreContentListQuery(
  input: ExploreContentListQueryAssemblyInput
): ExploreContentListQueryAssemblyResult {
  const parsed = parseExploreQueryFilters({
    query: input.query,
    tabFilters: input.tabFilters,
  });
  const activeContentTypes = parsed.contentTypes ?? input.savedDefinition?.contentTypes;
  const activeSections = parsed.sections ?? input.savedDefinition?.sections;
  const activeProjection = parsed.projection ?? input.savedDefinition?.projection;
  const requestProjection = activeProjection ?? 'CONTENT';
  const normalizedProjectionFilters = normalizeProjectionContentTypes({
    projection: activeProjection,
    contentTypes: activeContentTypes,
    contentTypesExplicitEmpty: parsed.contentTypesExplicitEmpty,
  });
  const resolvedSearch = parsed.search ?? input.savedDefinition?.search;
  const dateFrom = input.savedDefinition?.dateFrom ?? (parsed.after ? localMidnightIso(parsed.after) : undefined);
  const dateTo = input.savedDefinition?.dateTo ?? (parsed.before ? localMidnightIso(parsed.before) : undefined);

  return {
    request: {
      projection: requestProjection,
      contentTypes: normalizedProjectionFilters.contentTypes,
      contentTypesExplicitEmpty: normalizedProjectionFilters.contentTypesExplicitEmpty,
      sections: activeSections,
      obligations: input.savedDefinition?.obligations,
      createdByMemberIds: input.savedDefinition?.createdByMemberIds,
      tagIds: input.savedDefinition?.tagIds,
      search: resolvedSearch,
      dateFrom,
      dateTo,
      isRead: input.savedDefinition?.isRead,
      isStarred: input.savedDefinition?.isStarred,
      isFollowing: input.savedDefinition?.isFollowing,
      ...(!resolvedSearch || input.sortExplicit ? { orderBy: input.orderBy, orderDir: input.orderDir } : {}),
      cursor: input.cursor,
      limit: input.limit,
    },
    state: {
      projection: activeProjection,
      contentTypes: activeContentTypes,
      sections: activeSections,
      search: resolvedSearch,
      mailbox: parsed.mailbox,
      drives: parsed.drives,
      after: parsed.after,
      before: parsed.before,
    },
  };
}
